import { type CSSProperties, type ReactNode } from "react";

import { EditIcon, TrashIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import { OTHER_ROW_ID } from "@/constants/dataset";
import { cn } from "@/lib/utils";
import type {
  ColorTarget,
  MarkerImage,
  RowMarkerFields
} from "@/types/dataset";

import { Icon } from "equator-design-system";

import ColorPickerPanel from "./ColorPickerPanel";
import MarkerVisualPanel from "./MarkerVisualPanel";

/** Checkerboard pattern that signals a transparent fill (image mode). */
export const TRANSPARENT_CHECKERBOARD =
  "conic-gradient(#d1d5db 25%, #fff 0 50%, #d1d5db 0 75%, #fff 0) 0 0 / 10px 10px";

const SWATCH_CLASS =
  "size-9 shrink-0 rounded-md border border-gainsboro disabled:opacity-50";

/**
 * A single swatch button shared by the Custom Ordinal & Custom Breaks row and
 * "Other" swatches. Renders a flat color, or — as an image slot — the selected
 * icon, the uploaded image, or a transparent-checkerboard placeholder.
 */
export const ColorSwatchButton = ({
  testId,
  ariaLabel,
  active,
  disabled,
  onClick,
  color,
  image,
  iconId,
  isImageSlot = false
}: {
  testId: string;
  ariaLabel: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  color?: string;
  image?: string | null;
  iconId?: string | null;
  isImageSlot?: boolean;
}) => {
  // Swatch background: flat color; in the image slot, the uploaded image or
  // the checkerboard placeholder (an icon renders as a child, no background).
  let swatchStyle: CSSProperties | undefined = { backgroundColor: color };
  if (isImageSlot) {
    if (iconId) {
      swatchStyle = undefined;
    } else if (image) {
      swatchStyle = {
        backgroundImage: `url(${image})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      };
    } else {
      swatchStyle = { background: TRANSPARENT_CHECKERBOARD };
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(
        SWATCH_CLASS,
        "flex items-center justify-center",
        active && "ring-2 ring-yellow-400 ring-offset-0"
      )}
      style={swatchStyle}
    >
      {isImageSlot && iconId && (
        <Icon name={iconId} width={20} height={20} className="text-txtBody" />
      )}
    </button>
  );
};

/**
 * Column headers for the legend config panels. Shows a single "Stroke" column for
 * line datasets, "Fill" + "Stroke" otherwise (Stroke hidden in image mode). Set
 * `withActions` to reserve the trailing edit/delete columns (Ordinal & Breaks).
 */
export const LegendColumnHeaders = ({
  imageMode,
  strokeOnly,
  withActions
}: {
  imageMode?: boolean;
  strokeOnly?: boolean;
  withActions?: boolean;
}) => {
  const singleSwatch = imageMode || strokeOnly;
  const columns = withActions
    ? singleSwatch
      ? "grid-cols-[auto_1fr_auto_auto]"
      : "grid-cols-[auto_auto_1fr_auto_auto]"
    : singleSwatch
      ? "grid-cols-[auto_1fr]"
      : "grid-cols-[auto_auto_1fr]";
  return (
    <div
      className={cn(
        "grid items-center gap-2 text-[10px] uppercase tracking-wide text-grey",
        columns
      )}
    >
      <span className="w-9 text-center">{strokeOnly ? "Stroke" : "Fill"}</span>
      {!imageMode && !strokeOnly && (
        <span className="w-9 text-center">Stroke</span>
      )}
      <span className="text-left">Category</span>
    </div>
  );
};

/**
 * Fill (or image) + outline swatch pair for a legend data row. The outline
 * swatch is hidden in image mode (mirrors the on-map behaviour).
 */
export const LegendColorSwatches = ({
  imageMode,
  strokeOnly,
  image,
  iconId,
  fillColor,
  outlineColor,
  isFillActive,
  isOutlineActive,
  disabled,
  onFillClick,
  onOutlineClick,
  fillTestId,
  outlineTestId
}: {
  imageMode?: boolean;
  /** Line datasets: a single flat swatch (the stroke color) and no outline. */
  strokeOnly?: boolean;
  image?: string | null;
  /** Selected icon id, shown in the image slot; takes precedence over `image`. */
  iconId?: string | null;
  fillColor: string;
  outlineColor: string;
  isFillActive: boolean;
  isOutlineActive: boolean;
  disabled?: boolean;
  onFillClick: () => void;
  onOutlineClick: () => void;
  fillTestId: string;
  outlineTestId: string;
}) => (
  <>
    <ColorSwatchButton
      testId={fillTestId}
      ariaLabel={strokeOnly ? "Stroke color" : "Fill color"}
      active={isFillActive}
      disabled={disabled}
      onClick={onFillClick}
      color={fillColor}
      image={image}
      iconId={iconId}
      isImageSlot={!strokeOnly && !!imageMode}
    />
    {!imageMode && !strokeOnly && (
      <ColorSwatchButton
        testId={outlineTestId}
        ariaLabel="Outline color"
        active={isOutlineActive}
        disabled={disabled}
        onClick={onOutlineClick}
        color={outlineColor}
      />
    )}
  </>
);

/**
 * Row shell shared by Custom Ordinal & Custom Breaks: the fill/outline swatches,
 * a middle-content slot (`children` — the category name / range text), and the
 * edit + delete buttons. The per-method specifics (middle cell, delete-disabled
 * predicate, testid prefix) are passed in as props.
 */
export const LegendRow = ({
  imageMode,
  strokeOnly,
  image,
  iconId,
  fillColor,
  outlineColor,
  isFillActive,
  isOutlineActive,
  disabled,
  onFillClick,
  onOutlineClick,
  fillTestId,
  outlineTestId,
  onEdit,
  editAriaLabel,
  editTestId,
  onDelete,
  deleteAriaLabel,
  deleteTestId,
  deleteDisabled,
  children
}: {
  imageMode?: boolean;
  /** Line datasets: a single flat swatch (the stroke color) and no outline. */
  strokeOnly?: boolean;
  image?: string | null;
  iconId?: string | null;
  fillColor: string;
  outlineColor: string;
  isFillActive: boolean;
  isOutlineActive: boolean;
  disabled?: boolean;
  onFillClick: () => void;
  onOutlineClick: () => void;
  fillTestId: string;
  outlineTestId: string;
  onEdit: () => void;
  editAriaLabel: string;
  editTestId: string;
  onDelete: () => void;
  deleteAriaLabel: string;
  deleteTestId: string;
  deleteDisabled?: boolean;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "grid items-center gap-2",
      imageMode || strokeOnly
        ? "grid-cols-[auto_1fr_auto_auto]"
        : "grid-cols-[auto_auto_1fr_auto_auto]"
    )}
  >
    <LegendColorSwatches
      imageMode={imageMode}
      strokeOnly={strokeOnly}
      image={image}
      iconId={iconId}
      fillColor={fillColor}
      outlineColor={outlineColor}
      isFillActive={isFillActive}
      isOutlineActive={isOutlineActive}
      disabled={disabled}
      onFillClick={onFillClick}
      onOutlineClick={onOutlineClick}
      fillTestId={fillTestId}
      outlineTestId={outlineTestId}
    />
    <div className="flex flex-col overflow-hidden">{children}</div>
    <button
      type="button"
      onClick={onEdit}
      disabled={disabled}
      aria-label={editAriaLabel}
      data-testid={editTestId}
      className="text-grey hover:text-txtHeading disabled:opacity-50"
    >
      <EditIcon className="size-4 fill-txtPlaceholder" />
    </button>
    <button
      type="button"
      onClick={onDelete}
      disabled={disabled || deleteDisabled}
      aria-label={deleteAriaLabel}
      data-testid={deleteTestId}
      className="text-grey hover:text-cinnabar disabled:opacity-30"
    >
      <TrashIcon className="size-4 fill-txtPlaceholder" />
    </button>
  </div>
);

export const LegendOtherRow = ({
  testIdPrefix,
  strokeOnly,
  fillColor,
  outlineColor,
  isFillActive,
  isOutlineActive,
  disabled,
  onFillClick,
  onOutlineClick,
  className
}: {
  /** Prefix for the swatch test ids, e.g. "ordinal" / "breaks". */
  testIdPrefix: string;
  /** Line datasets: a single flat "Other" swatch (the stroke color). */
  strokeOnly?: boolean;
  fillColor: string;
  outlineColor: string;
  isFillActive: boolean;
  isOutlineActive: boolean;
  disabled?: boolean;
  onFillClick: () => void;
  onOutlineClick: () => void;
  className?: string;
}) => (
  <div className={className}>
    <p className="mb-1 text-[10px] uppercase tracking-wide text-grey">Other</p>
    <div className="flex items-center gap-2">
      <ColorSwatchButton
        testId={`${testIdPrefix}-other-fill`}
        ariaLabel={strokeOnly ? "Other stroke color" : "Other fill color"}
        active={isFillActive}
        disabled={disabled}
        onClick={onFillClick}
        color={fillColor}
      />
      {!strokeOnly && (
        <ColorSwatchButton
          testId={`${testIdPrefix}-other-outline`}
          ariaLabel="Other outline color"
          active={isOutlineActive}
          disabled={disabled}
          onClick={onOutlineClick}
          color={outlineColor}
        />
      )}
      <span className="text-[10px] text-grey">
        Includes all data not defined above
      </span>
    </div>
  </div>
);

/** Cancel / Save footer shared by the legend config panels. */
export const LegendActions = ({
  disabled,
  onCancel,
  onSave
}: {
  disabled?: boolean;
  onCancel: () => void;
  onSave: () => void;
}) => (
  <div className="flex items-center gap-2 pt-1">
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onCancel}
      disabled={disabled}
      className="h-7 flex-1 rounded-lg text-xs"
    >
      Cancel
    </Button>
    <Button
      type="button"
      size="sm"
      onClick={onSave}
      disabled={disabled}
      className="h-7 flex-1 rounded-lg text-xs"
    >
      Save
    </Button>
  </div>
);

/**
 * The bottom editing panel shared by Custom Ordinal & Custom Breaks: the Marker
 * Visual panel (image upload or icon select) when the active swatch is an image
 * slot, otherwise the color picker. Each is keyed by the active row so it
 * remounts (state resets) per clicked step.
 */
export const LegendActivePanel = ({
  activeColor,
  activeImage,
  activeIconId,
  activeColorValue,
  onImageApply,
  onIconApply,
  onImageRemove,
  onColorChange,
  onClose
}: {
  activeColor: ColorTarget | null;
  activeImage?: MarkerImage | null;
  activeIconId?: string | null;
  activeColorValue?: string;
  onImageApply: (image: MarkerImage) => void;
  onIconApply: (iconId: string) => void;
  onImageRemove?: () => void;
  onColorChange: (hex: string) => void;
  onClose: () => void;
}) => {
  if (!activeColor) return null;

  if (activeColor.target === "image") {
    return (
      <MarkerVisualPanel
        key={`${activeColor.rowId}-image`}
        initialImage={activeImage ?? null}
        initialIcon={activeIconId ?? null}
        onApply={onImageApply}
        onApplyIcon={onIconApply}
        onRemove={onImageRemove}
        onClose={onClose}
      />
    );
  }

  return (
    <ColorPickerPanel
      key={`${activeColor.rowId}-${activeColor.target}`}
      mode="color"
      color={activeColorValue ?? "#000000"}
      onColorChange={onColorChange}
      onClose={onClose}
    />
  );
};

/**
 * {@link LegendActivePanel} wired to a legend row: pre-selects the row's
 * image/icon and applies the picked marker via `updateRow`, keeping image and
 * icon mutually exclusive (ORB-3394). "Other" rows carry no marker, so applying
 * is a no-op for them. Shared by Custom Ordinal & Custom Breaks.
 */
export const LegendRowActivePanel = ({
  activeColor,
  activeRow,
  activeColorValue,
  updateRow,
  onColorChange,
  onClose
}: {
  activeColor: ColorTarget | null;
  /** The clicked row's marker fields (null for "Other" or no row). */
  activeRow: RowMarkerFields | null;
  activeColorValue?: string;
  updateRow: (rowId: string, patch: Partial<RowMarkerFields>) => void;
  onColorChange: (hex: string) => void;
  onClose: () => void;
}) => {
  const applyToActiveRow = (patch: Partial<RowMarkerFields>) => {
    if (!activeColor?.rowId || activeColor.rowId === OTHER_ROW_ID) return;
    updateRow(activeColor.rowId, patch);
  };

  return (
    <LegendActivePanel
      activeColor={activeColor}
      activeImage={
        activeRow?.image
          ? {
              url: activeRow.image,
              name: activeRow.imageName ?? "image",
              assetId: activeRow.imageAssetId
            }
          : null
      }
      activeIconId={activeRow?.iconId ?? null}
      activeColorValue={activeColorValue}
      onImageApply={(image) =>
        applyToActiveRow({
          image: image.url,
          imageName: image.name,
          imageAssetId: image.assetId,
          iconId: null
        })
      }
      onIconApply={(iconId) =>
        applyToActiveRow({
          iconId,
          image: null,
          imageName: null,
          imageAssetId: null
        })
      }
      onImageRemove={() =>
        applyToActiveRow({
          image: null,
          imageName: null,
          imageAssetId: null,
          iconId: null
        })
      }
      onColorChange={onColorChange}
      onClose={onClose}
    />
  );
};
